import axios from 'axios';

class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

}

class Semaphore {
  constructor(permits) {
    this.permits = permits;
    this.queue = [];

    this.acquire = () => {
      if (!this.permits) {
        const deferred = new Deferred();
        this.queue.push(deferred);
        return deferred.promise;
      }

      this.permits -= 1;
      return Semaphore.resolved;
    };

    this.release = () => {
      this.permits += 1;
      const deferred = this.queue.shift();

      if (!deferred) {
        return;
      }

      this.permits -= 1;
      deferred.resolve();
    };
  }

}
Semaphore.resolved = Promise.resolve();

class Mutex {
  constructor() {
    this.semaphore = new Semaphore(1);

    this.lock = () => this.semaphore.acquire();

    this.unlock = () => {
      this.semaphore.release();
    };
  }

}

const identity = arg => arg;

/* eslint-disable func-style */

function compose(...funcs) {
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-return */
  return funcs.length ? funcs.reduce((f, g) => (...args) => f(g(...args))) : identity;
}

const range = (begin, end, step = 1) => {
  const indices = [];

  for (let index = begin; index < end; index += step) {
    indices.push(index);
  }

  return indices;
};

const snakeCase = identifier => identifier.split(/(?=[A-Z])/u).join('_').toLowerCase();
const camelCase = identifier => identifier.split('_').map((word, index) => index ? `${word[0].toUpperCase()}${word.slice(1)}` : word).join('');

const mapKeys = (source, iteratee) => {
  const dist = {};
  Object.keys(source).forEach(key => {
    const value = source[key];
    dist[iteratee(key)] = value;
  });
  return dist;
};

class Store {
  constructor(options) {
    this.options = options;
    this.caches = Object.create(null);

    this.getItem = key => {
      const value = this.getValue(key);

      if (value) {
        try {
          const parsedValue = JSON.parse(value);

          if (!this.options) {
            return parsedValue.value;
          }

          const {
            name,
            storeName,
            maxAge
          } = this.options;

          if (Date.now() - parsedValue.timestamp < maxAge * 1000) {
            return parsedValue.value;
          }

          localStorage.removeItem(`${name}.${storeName}.${key}`);
          delete this.caches[key];
        } catch (_a) {}
      }

      return null;
    };

    this.setItem = (key, value) => {
      this.setValue(key, JSON.stringify({
        value,
        timestamp: Date.now()
      }));
      return value;
    };

    this.removeOutdatedCacheValues();
  }

  getValue(key) {
    if (this.options) {
      const {
        name,
        storeName
      } = this.options;
      const value = localStorage.getItem(`${name}.${storeName}.${key}`);

      if (value) {
        return value;
      }
    }

    if (this.caches[key]) {
      return this.caches[key];
    }

    return null;
  }

  setValue(key, value) {
    if (this.options) {
      const {
        name,
        storeName
      } = this.options;

      try {
        localStorage.setItem(`${name}.${storeName}.${key}`, value);
        return;
      } catch (_a) {}
    }

    this.caches[key] = value;
  }

  removeOutdatedCacheValues() {
    if (!this.options) {
      return;
    }

    const {
      name,
      storeName
    } = this.options;
    const keys = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      const prefix = `${name}.${storeName}.`;

      if (!key || key.indexOf(prefix)) {
        continue;
      }

      keys.push(key.slice(prefix.length));
    }

    keys.forEach(this.getItem);
  }

}

const cachable = (func, hash, options) => {
  const mutex = new Mutex();
  const store = new Store(options);
  return async (...args) => {
    var _a;

    const id = await hash(...args);

    try {
      await mutex.lock();
      return (_a = store.getItem(id)) !== null && _a !== void 0 ? _a : store.setItem(id, await func(...args));
    } finally {
      mutex.unlock();
    }
  };
};
const cachableSync = (func, hash) => {
  const store = Object.create(null);
  return (...args) => {
    var _a;

    const id = hash(...args);
    return (_a = store[id]) !== null && _a !== void 0 ? _a : store[id] = func(...args);
  };
};

const cancellableWith = cancelToken => func => {
  if (!cancelToken) {
    return func;
  }

  return async (...args) => {
    const invoking = func(...args);
    await Promise.race([invoking, cancelToken.promise]);
    cancelToken.throwIfRequested();
    return invoking;
  };
};

/**
 * @privateRemarks
 * IE10 does not support the file constructor, see {@link https://caniuse.com/#feat=fileapi}.
 *
 * @public
 */
const createFile = (blobParts, name, {
  lastModified: _lastModified = Date.now(),
  ...options
} = {}) => Object.assign(new Blob(blobParts, options), {
  name,
  lastModified: _lastModified
});

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

const isAxiosCancel = err => axios.isCancel(err);
const isAxiosError = err => !!(err === null || err === void 0 ? void 0 : err.isAxiosError);
const isUploadCancel = err => !!(err === null || err === void 0 ? void 0 : err.stats) && isAxiosCancel(err);
const isUploadError = err => !!(err === null || err === void 0 ? void 0 : err.stats) && isAxiosError(err);
/* eslint-enable @typescript-eslint/no-unsafe-member-access */

function enhanceError(err, {
  metadata,
  config,
  stats: {
    duration = 0,
    loaded = 0,
    retry = 0
  } = {}
}) {
  const stats = {
    duration,
    loaded,
    retry
  };

  if (isAxiosCancel(err) && !isUploadCancel(err) && config) {
    const uploadCancel = Object.assign(err, {
      config,
      stats
    });
    return uploadCancel;
  }

  if (isAxiosError(err) && !isUploadError(err) && metadata && config) {
    const {
      toJSON
    } = err;
    const uploadError = Object.assign(err, {
      metadata,
      config,
      stats,

      toJSON() {
        return { ...toJSON.call(this),
          metadata: this.metadata,
          config: this.config,
          stats: this.stats
        };
      }

    });
    return uploadError;
  }

  if ((isUploadCancel(err) || isUploadError(err)) && retry) {
    err.stats.retry += retry;
    return err;
  }

  return err;
}

const units = ['', 'K', 'M', 'G'];
const scale = 1 << 10;
const formatSize = (source, formatter = 1) => {
  if (isNaN(source) || !isFinite(source) || source < 0) {
    return source.toString();
  }

  let size = source;
  let index = 0;

  while (size >= scale && index < units.length - 1) {
    size /= scale;
    index += 1;
  }

  if (typeof formatter === 'function') {
    return formatter(size, units[index], index, units);
  }

  return index ? `${size.toFixed(formatter)} ${units[index]}B` : `${size} bytes`;
};
const parseSize = cachableSync(source => {
  if (typeof source === 'number') {
    return source;
  }

  const regex = /\s*[+-]?(?:\d*\.?\d+(?:[Ee][+-]?\d*)?|NaN|Infinity)/u;
  const size = parseFloat(source);
  const unit = source.replace(regex, '').toUpperCase().replace('B', '').trim();
  const index = units.indexOf(unit);
  return ~index ? size * scale ** index : size;
}, source => `${source}`);
const getFileSize = cachableSync(({
  file
}) => file.size, ({
  id
}) => id);

const getMetadata = cachable(config => {
  const {
    metadata,
    cancelTokenSource
  } = config;
  const cancellable = cancellableWith(cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token);
  return typeof metadata === 'function' ? cancellable(metadata)(config) : metadata;
}, ({
  id
}) => id);

const createProgressEvent = config => {
  try {
    return new ProgressEvent('progress', config);
    /* eslint-disable no-inline-comments */
  } catch (err)
  /* istanbul ignore next: not possible to test */
  {
    /* eslint-enable no-inline-comments */
    const {
      lengthComputable,
      loaded,
      total
    } = config;
    const event = document.createEvent('ProgressEvent');
    event.initProgressEvent('progress', true, true, lengthComputable, loaded, total);
    return event;
  }
};
const enhanceProgressEventWith = ({
  config,
  error,
  percent: _percent = NaN,
  speed: _speed = NaN,
  timeRemaining: _timeRemaining = NaN,
  isInstant: _isInstant = false
}) => event => Object.assign(event, {
  config,
  error,
  percent: _percent,
  speed: _speed,
  timeRemaining: _timeRemaining,
  isInstant: _isInstant
});

class Progress {
  constructor(props) {
    this.props = props;
    this.loaded = 0;
    this.total = 0;
    this.snapshot = {
      timeStamp: NaN,
      loaded: 0,
      speed: NaN,
      timer: NaN
    };
    this.loadedOf = {};

    this.next = event => {
      var _a;

      if (event.error || !event.lengthComputable) {
        return;
      }

      const {
        timeStamp,
        config,
        isInstant
      } = event;
      const {
        id,
        chunk
      } = config;
      const chunkId = (_a = chunk === null || chunk === void 0 ? void 0 : chunk.index) !== null && _a !== void 0 ? _a : 0;

      if (!this.loadedOf[id] && chunkId) {
        return;
      }

      const {
        onProgress,
        windowTime = 1000
      } = this.props;

      if (isNaN(this.snapshot.timeStamp) && !isInstant) {
        this.snapshot.timeStamp = timeStamp;
      }

      if (!this.loadedOf[id]) {
        this.total += chunk ? getFileSize(config) : event.total;
        this.loadedOf[id] = {
          size: 0,
          chunks: {}
        };
      }

      if (!this.loadedOf[id].chunks[chunkId]) {
        this.loadedOf[id].chunks[chunkId] = 0;
      }

      const delta = event.loaded - this.loadedOf[id].chunks[chunkId];
      this.loaded += delta;
      this.loadedOf[id].size += delta;
      this.loadedOf[id].chunks[chunkId] += delta;

      if (isInstant) {
        this.snapshot.loaded += delta;
      }

      if (timeStamp - this.snapshot.timeStamp >= windowTime || this.loaded === this.total) {
        window.clearTimeout(this.snapshot.timer);

        const resetSpeed = () => {
          if (!this.loadedOf[id]) {
            return;
          }

          this.snapshot.speed = 0;
          onProgress(this.createEvent({
            config
          }));
        };

        this.snapshot.speed = this.loaded === this.total ? NaN : Math.max((this.loaded - this.snapshot.loaded) / (timeStamp - this.snapshot.timeStamp), 0);

        if (windowTime && this.snapshot.loaded < this.loaded && this.loaded < this.total) {
          this.snapshot.timer = window.setTimeout(resetSpeed, windowTime << 1);
        }

        this.snapshot.loaded = this.loaded;
        this.snapshot.timeStamp = timeStamp;
      }

      onProgress(this.createEvent({
        config,
        isInstant
      }));
    };

    this.complete = config => {
      if (this.loaded === this.total) {
        return;
      }

      this.loaded = this.total;
      this.snapshot.loaded = this.total;
      const {
        onProgress
      } = this.props;
      onProgress(this.createEvent({
        config
      }));
    };

    this.error = error => {
      if (!(isUploadCancel(error) || isUploadError(error))) {
        return;
      }

      const {
        config
      } = error;
      const {
        id
      } = config;

      if (!this.loadedOf[id]) {
        return;
      }

      const {
        onProgress
      } = this.props;
      window.clearTimeout(this.snapshot.timer);
      const size = getFileSize(config);
      this.loaded -= this.loadedOf[id].size;

      if (isUploadCancel(error)) {
        this.total -= size;
      } else {
        this.loaded += size;
      }

      delete this.loadedOf[id];

      if (this.loaded === this.total) {
        this.snapshot.speed = NaN;
      }

      this.snapshot.loaded = this.loaded;
      this.snapshot.timeStamp = NaN;
      onProgress(this.createEvent({
        config,
        error
      }));
    };
  }

  createEvent({
    config,
    ...restOptions
  }) {
    const {
      chunk,
      ...restConfig
    } = config;
    const {
      loaded,
      total,
      snapshot
    } = this;
    return enhanceProgressEventWith({ ...restOptions,
      config: restConfig,
      percent: loaded / total * 100,
      speed: snapshot.speed,
      timeRemaining: (total - snapshot.loaded) / snapshot.speed
    })(createProgressEvent({
      lengthComputable: true,
      loaded,
      total
    }));
  }

}

const retryable = (retryWhen, onRetry) => func => {
  if (!retryWhen) {
    return func;
  }

  let errors = [];
  return async (...args) => {
    while (true) {
      try {
        return await func(...args);
      } catch (err) {
        errors = errors.concat(err);

        if (!(await retryWhen(errors))) {
          throw err;
        }
      }

      onRetry === null || onRetry === void 0 ? void 0 : onRetry(errors);
    }
  };
};

const sliceFile = (blob, ...args) => {
  if (typeof blob.mozSlice === 'function') {
    return blob.mozSlice(...args);
  }

  if (typeof blob.webkitSlice === 'function') {
    return blob.webkitSlice(...args);
  }

  return blob.slice(...args);
};

const instance = axios.create({
  withCredentials: false
});
const request = async config => {
  const {
    params
  } = config;
  const response = await instance.request({ ...config,
    params: params && typeof params === 'object' ? mapKeys(params, snakeCase) : params
  });
  const {
    data
  } = response;
  return { ...response,
    data: data && typeof data === 'object' ? mapKeys(data, camelCase) : data
  };
};

const getConfigs = async config => {
  const {
    file,
    chunk,
    timeout,
    cancelTokenSource,
    onProgress
  } = config;
  const metadata = await getMetadata(config);
  const {
    uploadToken,
    endpoints: [endpoint]
  } = metadata;
  const method = 'POST';
  const baseURL = `https://${endpoint}`;
  const url = `/api/upload${chunk ? '/fragment' : ''}`;
  const headers = {
    'Content-Type': 'application/octet-stream'
  };
  const params = {
    uploadToken
  };

  if (chunk) {
    const {
      range,
      index
    } = chunk;
    const fileSize = getFileSize(config);
    headers['Content-Range'] = `bytes ${range[0]}-${range[1] - 1}/${fileSize}`;
    params.fragmentId = `${index}`;
  }

  const data = chunk ? createFile([sliceFile(file, ...chunk.range)], `${file.name}-${chunk.index}`) : file;
  const uploadConfig = { ...config,
    metadata
  };
  const requestConfig = {
    method,
    baseURL,
    url,
    headers,
    params,
    data,
    timeout: chunk && timeout,
    cancelToken: cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token,
    onUploadProgress: onProgress && compose(onProgress, enhanceProgressEventWith({
      config: uploadConfig
    }))
  };
  return {
    requestConfig,
    uploadConfig
  };
};

const upload = async config => {
  const {
    requestConfig,
    uploadConfig
  } = await getConfigs(config);
  const {
    metadata
  } = uploadConfig;
  const startTime = Date.now();

  try {
    const response = await request(requestConfig);
    return { ...response,
      metadata,
      config: uploadConfig,
      stats: {
        duration: Date.now() - startTime,
        loaded: requestConfig.data.size,
        retry: 0
      }
    };
  } catch (err) {
    enhanceError(err, {
      metadata,
      config: uploadConfig,
      stats: {
        duration: Date.now() - startTime
      }
    });
    throw err;
  }
};

const createBaseUploader = () => ({
  upload,
  cancel: () => true
});

const withCancel = () => {
  const filenames = Object.create(null);
  const cancelTokenSources = Object.create(null);
  return uploader => ({ ...uploader,
    upload: async config => {
      const {
        id,
        file,
        cancelTokenSource = axios.CancelToken.source()
      } = config;
      filenames[id] = file.name;
      cancelTokenSources[id] = cancelTokenSource;

      try {
        const response = await uploader.upload({ ...config,
          cancelTokenSource
        });
        return response;
      } catch (err) {
        if (isUploadError(err)) {
          cancelTokenSources[id].cancel();
        }

        throw err;
      }
    },
    cancel: id => {
      const cancelTokenSource = cancelTokenSources[id];

      if (!cancelTokenSource) {
        return false;
      }

      cancelTokenSource.cancel(`Cancel: ${filenames[id]} upload cancelled.`);
      return uploader.cancel(id);
    }
  });
};

const withConcurrent = ({
  concurrent: _concurrent = 0
} = {}) => {
  if (_concurrent <= 0 || !isFinite(_concurrent)) {
    return identity;
  }

  const semaphore = new Semaphore(_concurrent);
  return uploader => ({ ...uploader,
    upload: async config => {
      const {
        cancelTokenSource
      } = config;
      const cancellable = cancellableWith(cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token);
      const acquire = cancellable(semaphore.acquire);
      let transmitted = false;

      try {
        await acquire();
        const response = await uploader.upload({ ...config,
          onProgress: event => {
            var _a;

            (_a = config.onProgress) === null || _a === void 0 ? void 0 : _a.call(config, event);
            const {
              lengthComputable,
              loaded,
              total
            } = event;

            if (!lengthComputable || loaded < total) {
              return;
            }

            transmitted = true;
            semaphore.release();
          }
        });
        return response;
      } finally {
        transmitted || semaphore.release();
      }
    }
  });
};

const withChunkUpload = ({
  shouldFileSplit: _shouldFileSplit = true,
  chunkSize: size = '1M',
  chunkConcurrent: _chunkConcurrent = 3,
  retryWhen: _retryWhen = () => false
} = {}) => uploader => ({ ...uploader,
  upload: async config => {
    const {
      file,
      cancelTokenSource
    } = config;
    const cancellable = cancellableWith(cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token);
    const fileSize = getFileSize(config);
    const chunkSize = Math.min(parseSize(typeof size === 'function' ? await cancellable(size)(config) : size), parseSize('200M'));
    const shouldSplitFile = await (async () => {
      if (chunkSize <= 0) {
        return false;
      }

      if (typeof _shouldFileSplit === 'function') {
        const predication = await cancellable(_shouldFileSplit)(config);
        return predication;
      }

      if (typeof _shouldFileSplit === 'number' || typeof _shouldFileSplit === 'string') {
        return fileSize >= parseSize(_shouldFileSplit);
      }

      return !!_shouldFileSplit;
    })();

    if (!shouldSplitFile) {
      return uploader.upload(config);
    }

    const metadata = await getMetadata(config);
    const {
      uploadToken,
      endpoints: [endpoint]
    } = metadata;
    const baseURL = `https://${endpoint}`;
    const stats = {
      duration: 0,
      loaded: 0,
      retry: 0
    };
    const retryableRequest = retryable(cancellable(_retryWhen), () => stats.retry += 1)(request);

    const upload = async configWithMetadata => {
      const {
        onProgress
      } = configWithMetadata;
      const progress = onProgress && new Progress({
        onProgress,
        windowTime: 0
      });
      const resumeResponse = await retryableRequest({
        baseURL,
        url: '/api/upload/resume',
        params: {
          uploadToken
        },
        cancelToken: cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token
      });
      /* istanbul ignore next: compatible with legacy uploader, deprecated soon */

      const {
        data: {
          existed,
          fragmentIndex,
          fragmentList = range(0, fragmentIndex + 1).map(id => ({
            id
          }))
        }
      } = resumeResponse;
      const finishedChunks = fragmentList.reduce((finishedChunks, {
        id
      }) => ({ ...finishedChunks,
        [id]: true
      }), {});
      const chunkUploader = withConcurrent({
        concurrent: _chunkConcurrent
      })(uploader);

      const uploadChunk = async chunkUploadConfig => {
        const response = await chunkUploader.upload(chunkUploadConfig);
        const {
          loaded,
          retry
        } = response.stats;
        stats.loaded += loaded;
        stats.retry += retry;
        return response;
      };

      const maxUploadingChunks = 32;
      let index = 0;
      let begin = 0;

      while (begin < fileSize) {
        const chunkUploadConfigs = [];

        while (begin < fileSize && chunkUploadConfigs.length < maxUploadingChunks) {
          const range = [begin, Math.min(begin + chunkSize, fileSize)];
          const chunkUploadConfig = { ...configWithMetadata,
            file,
            chunk: {
              range,
              index
            },
            onProgress: progress === null || progress === void 0 ? void 0 : progress.next
          };

          if (!finishedChunks[index]) {
            chunkUploadConfigs.push(chunkUploadConfig);
          } else if (progress) {
            const chunkSize = range[1] - range[0];
            compose(progress.next, enhanceProgressEventWith({
              config: chunkUploadConfig,
              isInstant: true
            }), createProgressEvent)({
              lengthComputable: true,
              loaded: chunkSize,
              total: chunkSize
            });
          }

          index += 1;
          begin += chunkSize;
        }

        await Promise.all(chunkUploadConfigs.map(uploadChunk));
      }

      progress === null || progress === void 0 ? void 0 : progress.complete(configWithMetadata);

      if (existed) {
        return resumeResponse;
      }

      const completeResponse = await retryableRequest({
        method: 'POST',
        baseURL,
        url: '/api/upload/complete',
        params: {
          fragmentCount: index,
          uploadToken
        },
        cancelToken: cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token
      });
      return completeResponse;
    };

    const configWithMetadata = { ...config,
      metadata
    };
    const startTime = Date.now();

    try {
      const response = await upload(configWithMetadata);
      stats.duration = Date.now() - startTime;
      return { ...response,
        metadata,
        config: configWithMetadata,
        stats
      };
    } catch (err) {
      stats.duration = Date.now() - startTime;
      enhanceError(err, {
        metadata,
        config: configWithMetadata,
        stats
      });
      throw err;
    }
  }
});

const withFileUpload = () => uploader => ({ ...uploader,
  upload: async config => {
    const {
      onProgress
    } = config;

    if (!onProgress) {
      return uploader.upload(config);
    }

    const progress = new Progress({
      onProgress
    });

    try {
      const response = await uploader.upload({ ...config,
        onProgress: progress.next
      });
      progress.complete(response.config);
      return response;
    } catch (err) {
      progress.error(enhanceError(err, {
        config
      }));
      throw err;
    }
  }
});

const withRetry = ({
  retryWhen
} = {}) => {
  if (!retryWhen) {
    return identity;
  }

  return uploader => ({ ...uploader,
    upload: async config => {
      let retry = 0;

      const onRetry = () => retry += 1;

      const {
        cancelTokenSource
      } = config;
      const cancellable = cancellableWith(cancelTokenSource === null || cancelTokenSource === void 0 ? void 0 : cancelTokenSource.token);
      const retryableUpload = retryable(cancellable(retryWhen), onRetry)(uploader.upload);

      try {
        const response = await retryableUpload(config);
        response.stats.retry += retry;
        return response;
      } catch (err) {
        enhanceError(err, {
          config,
          stats: {
            retry
          }
        });
        throw err;
      }
    }
  });
};

const withTotalProgress = ({
  onProgress
} = {}) => {
  if (!onProgress) {
    return identity;
  }

  const progress = new Progress({
    onProgress
  });
  const requests = Object.create(null);
  const finished = Object.create(null);
  return uploader => ({ ...uploader,
    upload: async config => {
      const {
        id
      } = config;
      requests[id] = config;
      compose(progress.next, enhanceProgressEventWith({
        config
      }), createProgressEvent)({
        lengthComputable: true,
        loaded: 0,
        total: getFileSize(config)
      });

      try {
        const response = await uploader.upload({ ...config,
          onProgress: event => {
            var _a;

            (_a = config.onProgress) === null || _a === void 0 ? void 0 : _a.call(config, event);
            progress.next(event);
          }
        });
        return response;
      } catch (err) {
        progress.error(enhanceError(err, {
          config
        }));
        throw err;
      } finally {
        finished[id] = true;
      }
    },
    cancel: id => {
      var _a;

      const config = requests[id];
      const err = (_a = config === null || config === void 0 ? void 0 : config.cancelTokenSource) === null || _a === void 0 ? void 0 : _a.token.reason;

      if (err && finished[id]) {
        progress.error(enhanceError(err, {
          config
        }));
      }

      return uploader.cancel(id);
    }
  });
};

const createUploader = ({
  onProgress,
  concurrent,
  shouldFileSplit,
  chunkSize,
  chunkConcurrent,
  retryWhen
} = {}) => compose(withCancel(), withTotalProgress({
  onProgress
}), withFileUpload(), withConcurrent({
  concurrent
}), withChunkUpload({
  shouldFileSplit,
  chunkSize,
  chunkConcurrent,
  retryWhen
}), withRetry({
  retryWhen
}))(createBaseUploader());

export { createFile, createUploader, formatSize, isUploadCancel, isUploadError, parseSize, sliceFile };
